/**
 * Australian formatting helpers — see docs/08-glossary-au.md §4.
 *
 * Dates are `d/m/Y` or `j M Y`, never US ordering. Times render in the project's
 * timezone rather than the viewer's, so every function takes an optional
 * timeZone and callers pass the active project's zone.
 */

/**
 * Fallback site timezone, matching `app.site_timezone` on the API.
 * Every formatter takes an override so a screen can render in the active
 * project's timezone once per-project timezones are threaded through — a Perth
 * admin reviewing a Sydney site must see Sydney times, never their own.
 */
const DEFAULT_TZ = 'Australia/Melbourne';

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `07/08/2026` */
export function formatDate(value: string | Date | null | undefined, timeZone = DEFAULT_TZ): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone,
  }).format(date);
}

/** `07 Aug 2026` — the form used across cards, timelines and tables. */
export function formatDateLong(value: string | Date | null | undefined, timeZone = DEFAULT_TZ): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(date);
}

/** `7:02 AM` */
export function formatTime(value: string | Date | null | undefined, timeZone = DEFAULT_TZ): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  })
    .format(date)
    .replace(/\s?(am|pm)/i, (m) => ` ${m.trim().toUpperCase()}`);
}

/** `07 Aug 2026 · 7:02 AM` */
export function formatDateTime(value: string | Date | null | undefined, timeZone = DEFAULT_TZ): string {
  const date = toDate(value);
  if (!date) return '—';
  return `${formatDateLong(date, timeZone)} · ${formatTime(date, timeZone)}`;
}

/** `Today · 10:42 AM`, `Yesterday · 2:11 PM`, else `05 Aug · 4:22 PM`. */
export function formatRelativeDay(
  value: string | Date | null | undefined,
  now: Date = new Date(),
  timeZone = DEFAULT_TZ,
): string {
  const date = toDate(value);
  if (!date) return '—';

  const dayKey = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).format(d);

  const yesterday = new Date(now.getTime() - 86_400_000);
  const time = formatTime(date, timeZone);

  if (dayKey(date) === dayKey(now)) return `Today · ${time}`;
  if (dayKey(date) === dayKey(yesterday)) return `Yesterday · ${time}`;

  const short = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', timeZone }).format(date);
  return `${short} · ${time}`;
}

/** `0412 345 678` for display; storage stays E.164. */
export function formatMobile(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');
  const local = digits.startsWith('61') ? `0${digits.slice(2)}` : digits;
  if (local.length === 10 && local.startsWith('04')) {
    return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
  }
  return value;
}

/** `2,847` */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-AU').format(value);
}

/** `$1,234.56` */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
}

/** `8h 12m` from a minute count. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
}

/**
 * The suffix a KPI's unit puts after its number.
 *
 * Two conventions reached the same column. The seeder and the CEO dashboard
 * speak in words — `percent` — while the Company editor's unit box is a free
 * text field with `%` for a placeholder, so whatever somebody types is stored
 * literally. Reading the column raw printed "40percent" on the Company
 * scorecard; mapping only the word dropped the suffix for anyone who typed the
 * symbol.
 *
 * So both are understood and both render the same, and nothing has to be
 * migrated for a figure to read correctly on every screen that shows it.
 */
export function kpiUnitSuffix(unit: string | null | undefined): string {
  if (!unit) return '';

  const word = unit.trim().toLowerCase();

  return word === 'percent' || word === '%' ? '%' : unit;
}

/** `MC` from "Michael Carter". Two letters maximum. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Days until a date. Negative once past. */
export function daysUntil(value: string | Date | null | undefined, now: Date = new Date()): number | null {
  const date = toDate(value);
  if (!date) return null;
  return Math.ceil((date.getTime() - now.getTime()) / 86_400_000);
}
